import React, {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { JSX } from 'react';
import Map, {
  Marker,
  Source,
  Layer,
  GeolocateControl,
  MapRef,
  AttributionControl,
} from 'react-map-gl/maplibre';
import * as maplibregl from 'maplibre-gl';
import { setWorkerUrl } from 'maplibre-gl';
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import 'maplibre-gl/dist/maplibre-gl.css';
import { StoryPointStatus } from 'magical-trails';

import { generateMarkerIcon } from './icon';
import { prefetchMapTiles } from '../../utils/prefetchMapTiles';

// MapLibre 6 no longer inlines its web worker: it ships dist/
// maplibre-gl-worker.mjs as a separate ESM file and, unless told otherwise,
// resolves it at runtime as `new URL('./maplibre-gl-worker.mjs',
// import.meta.url)` — i.e. next to the bundled chunk, under /assets/. Nothing
// emits a file at that path, so the SPA's index.html fallback answers with 200
// text/html, the module worker fails to parse, and the map paints only the
// style's background colour: no roads, no labels, no tiles (this shipped to
// production in PR #100's maplibre 6 bump). Pointing setWorkerUrl at a chunk
// Vite actually emits fixes it; this must run before any Map is constructed,
// hence module scope rather than an effect. TrailMap is the only component in
// this app that constructs a <Map>, so one call here covers the whole player.
//
// `?worker&url` rather than plain `?url`: the dist worker imports its sibling
// maplibre-gl-shared.mjs, and `?url` would emit the worker verbatim without
// that sibling, so it would still fail on its first import. `?worker&url`
// routes it through Vite's worker pipeline, emitting a self-contained chunk.
setWorkerUrl(maplibreWorkerUrl);

export type LngLat = [number, number]; // [lng, lat]

export interface StoryPointLite {
  id: string;
  code?: string | null;
  coordinates: LngLat;
  status: StoryPointStatus;
}

export interface ImageOverlayProps {
  id: string;
  url: string;
  // Four corner coords: top-left, top-right, bottom-right, bottom-left
  coordinates: [LngLat, LngLat, LngLat, LngLat];
  opacity?: number;
}

export interface TrailMapProps {
  points: StoryPointLite[];
  selectedPointId?: string;
  onMarkerClick?: (id: string) => void;
  showUserLocation?: boolean;
  showAccuracyCircle?: boolean;
  // Optional geofence visualization
  showGeofence?: boolean;
  geofencePoints?: Pick<StoryPointLite, 'id' | 'coordinates'>[]; // defaults to points
  geofenceRadiusMeters?: number; // visual circle radius (approx), default ~10m
  imageOverlays?: ImageOverlayProps[];
  className?: string;
}

export interface TrailMapHandle {
  zoomIn: () => void;
  zoomOut: () => void;
  fitToBounds: (
    bounds: [[number, number], [number, number]],
    padding?: { top: number; left: number; right: number; bottom: number }
  ) => void;
  getMap: () => any | null;
}

const computeBounds = (
  coords: LngLat[]
): [[number, number], [number, number]] | null => {
  if (!coords.length) return null;
  let minLng = Infinity,
    minLat = Infinity,
    maxLng = -Infinity,
    maxLat = -Infinity;
  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng;
    if (lat < minLat) minLat = lat;
    if (lng > maxLng) maxLng = lng;
    if (lat > maxLat) maxLat = lat;
  }
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
};

const buildCirclePolygon = (
  center: LngLat,
  radiusMeters: number,
  steps = 64
): [number, number][][] => {
  const [lng, lat] = center;
  const latRad = (lat * Math.PI) / 180;
  const radiusRad = radiusMeters / 6378137; // Earth radius in meters
  const coords: [number, number][] = [];

  for (let i = 0; i <= steps; i++) {
    const angle = (2 * Math.PI * i) / steps;
    const latOffset = Math.asin(
      Math.sin(latRad) * Math.cos(radiusRad) +
        Math.cos(latRad) * Math.sin(radiusRad) * Math.cos(angle)
    );
    const lngOffset =
      lng +
      (Math.atan2(
        Math.sin(angle) * Math.sin(radiusRad) * Math.cos(latRad),
        Math.cos(radiusRad) - Math.sin(latRad) * Math.sin(latOffset)
      ) *
        180) /
        Math.PI;

    coords.push([((lngOffset + 540) % 360) - 180, (latOffset * 180) / Math.PI]);
  }

  return [coords];
};

// Consistent padding for map bounds: more bottom space for trail points, right for buttons
const MAP_PADDING = { top: 40, left: 40, right: 80, bottom: 200 };

const TrailMap = React.forwardRef<TrailMapHandle, TrailMapProps>(
  (
    {
      points,
      selectedPointId,
      onMarkerClick,
      showUserLocation = true,
      showAccuracyCircle = false,
      showGeofence = false,
      geofencePoints,
      geofenceRadiusMeters = 10,
      imageOverlays,
      className,
    },
    ref
  ): JSX.Element => {
    const mapRef = useRef<MapRef | null>(null);
    const geolocateControlRef = useRef<maplibregl.GeolocateControl | null>(
      null
    );
    const [mounted, setMounted] = useState(false);
    const styleUrl = 'https://tiles.openfreemap.org/styles/liberty';
    const [mapLoaded, setMapLoaded] = useState(false);

    const getMapInstance = (): any | null => {
      const r: any = mapRef.current as any;
      if (!r) return null;
      return typeof r.getMap === 'function' ? r.getMap() : r;
    };

    useImperativeHandle(
      ref,
      () => ({
        zoomIn: () => {
          const m = getMapInstance();
          if (m) m.zoomIn();
        },
        zoomOut: () => {
          const m = getMapInstance();
          if (m) m.zoomOut();
        },
        fitToBounds: (bounds, padding) => {
          const m = getMapInstance();
          if (m && bounds) {
            const pad = padding ?? MAP_PADDING;
            // Set bearing/pitch BEFORE fitBounds to avoid interrupting the animation
            m.setBearing(0);
            m.setPitch(0);
            m.fitBounds(bounds, { padding: pad });
          }
        },
        getMap: () => getMapInstance(),
      }),
      []
    );

    const bounds = useMemo(
      () => computeBounds(points.map((p) => p.coordinates)),
      [points]
    );

    // Warm the base-map cache around this trail's bounds while online, so the
    // map still renders if the player later goes offline in the field (the map
    // itself only lazily caches the tiles the user actually pans over). Runs
    // once per trail (keyed on the bounds signature); best-effort, never blocks.
    const prefetchedBoundsRef = useRef<string | null>(null);
    useEffect(() => {
      if (!bounds) return;
      const run = () => {
        // Skip while offline — but do NOT record the ref, so a later retry
        // still fires. The ref is recorded only when an online prefetch is
        // actually initiated (dedup so we prefetch each trail's bounds once).
        if (typeof navigator !== 'undefined' && navigator.onLine === false)
          return;
        const sig = JSON.stringify(bounds);
        if (prefetchedBoundsRef.current === sig) return;
        prefetchedBoundsRef.current = sig;
        void prefetchMapTiles(bounds);
      };
      run();
      // A player who opened the trail with no signal gets nothing above; retry
      // when connectivity returns so the map still warms for later offline use.
      window.addEventListener('online', run);
      return () => window.removeEventListener('online', run);
    }, [bounds]);

    // Compute initial view state from bounds to avoid world-view flash on load
    const initialViewState = useMemo(() => {
      if (bounds) {
        const [[minLng, minLat], [maxLng, maxLat]] = bounds;
        const centerLng = (minLng + maxLng) / 2;
        const centerLat = (minLat + maxLat) / 2;
        // Estimate a reasonable zoom level based on the span
        const lngSpan = maxLng - minLng;
        const latSpan = maxLat - minLat;
        const maxSpan = Math.max(lngSpan, latSpan, 0.0001); // avoid div by 0
        // Rough approximation: zoom ~14 for small areas, lower for larger
        const zoom = Math.max(1, Math.min(18, 13 - Math.log2(maxSpan * 50)));
        return {
          longitude: centerLng,
          latitude: centerLat,
          zoom,
          bearing: 0,
          pitch: 0,
        };
      }
      return { longitude: 0, latitude: 0, zoom: 1, bearing: 0, pitch: 0 };
    }, [bounds]);

    useEffect(() => {
      if (!mapLoaded || !bounds) return;
      const map = mapRef.current?.getMap();
      if (!map) return;
      try {
        map.fitBounds(bounds, { padding: MAP_PADDING });
      } catch {}
    }, [bounds, mapLoaded]);

    useEffect(() => {
      if (!mapLoaded || !selectedPointId) return;
      const map = mapRef.current?.getMap();
      if (!map) return;
      const sel = points.find(
        (p) => p.id === selectedPointId || p.code === selectedPointId
      );
      if (!sel) return;
      try {
        map.easeTo({
          center: sel.coordinates,
          zoom: Math.max(map.getZoom(), 18),
        });
      } catch {}
    }, [selectedPointId, points, mapLoaded]);

    const geofenceGeoJSON = useMemo(() => {
      if (!showGeofence) return null;
      const targets = geofencePoints ?? points;
      if (!targets.length) return null;

      return {
        type: 'FeatureCollection',
        features: targets.map((p) => ({
          type: 'Feature',
          properties: { id: p.id },
          geometry: {
            type: 'Polygon',
            coordinates: buildCirclePolygon(
              p.coordinates,
              geofenceRadiusMeters
            ),
          },
        })),
      } as const;
    }, [geofencePoints, geofenceRadiusMeters, points, showGeofence]);

    // Scale the visual circle to approximate meters across zoom levels using a zoom-to-radius mapping.
    const geofenceFillLayer: any = useMemo(() => {
      if (!showGeofence) return null;
      return {
        id: 'geofence-layer',
        type: 'fill',
        paint: {
          'fill-color': '#2563eb',
          'fill-opacity': 0.12,
        },
      };
    }, [showGeofence]);

    const geofenceStrokeLayer: any = useMemo(() => {
      if (!showGeofence) return null;
      return {
        id: 'geofence-layer-outline',
        type: 'line',
        paint: {
          'line-color': '#2563eb',
          'line-width': 1.5,
          'line-opacity': 0.35,
        },
      };
    }, [showGeofence]);

    useEffect(() => {
      // Ensure client-only mount (avoids container undefined issues)
      setMounted(true);
    }, []);

    // If you want to try OpenFreeMap again later, swap the style URL above or add a runtime check here.

    // no prefetch: pass URL string directly (maplibre supports URL or spec)

    return (
      <div className={className} style={{ position: 'relative' }}>
        {mounted && (
          <Map
            key={styleUrl}
            ref={mapRef}
            initialViewState={initialViewState}
            // OpenFreeMap vector style
            mapStyle={styleUrl}
            style={{ width: '100%', height: '100%' }}
            attributionControl={false}
            mapLib={maplibregl}
            onLoad={() => {
              setMapLoaded(true);
              // Immediately fit to current points with normalized camera on first load
              const m = mapRef.current?.getMap();
              if (m && bounds) {
                const camera = m.cameraForBounds(bounds, {
                  padding: MAP_PADDING,
                });
                if (camera) {
                  m.jumpTo({ ...camera, bearing: 0, pitch: 0 });
                } else {
                  m.fitBounds(bounds, { padding: MAP_PADDING });
                  m.setBearing(0);
                  m.setPitch(0);
                }
              }
            }}
          >
            {mapLoaded && showUserLocation && (
              <GeolocateControl
                ref={(ref) => {
                  // Store reference and auto-trigger when the control is
                  // ready — but ONLY when geolocation permission was already
                  // granted. Triggering unconditionally used to summon the
                  // browser's own permission prompt with no user gesture,
                  // possibly right underneath the GDPR consent overlay. If
                  // the Permissions API is unavailable, skip the
                  // auto-trigger entirely rather than risk an unsolicited
                  // prompt.
                  if (ref && !geolocateControlRef.current) {
                    geolocateControlRef.current = ref;
                    if (
                      typeof navigator !== 'undefined' &&
                      navigator.permissions?.query
                    ) {
                      navigator.permissions
                        .query({ name: 'geolocation' })
                        .then((status) => {
                          if (status.state !== 'granted') return;
                          // Trigger after a small delay to ensure the control is fully initialized
                          setTimeout(() => {
                            try {
                              geolocateControlRef.current?.trigger?.();
                            } catch {
                              // Ignore errors if trigger fails
                            }
                          }, 100);
                        })
                        .catch(() => {
                          // Permissions query unsupported/failed — don't
                          // auto-trigger.
                        });
                    }
                  }
                }}
                position="top-left"
                trackUserLocation
                showUserLocation
                showAccuracyCircle={showAccuracyCircle}
              />
            )}

            {mapLoaded &&
              points.map((p) => (
                <Marker
                  key={p.id}
                  longitude={p.coordinates[0]}
                  latitude={p.coordinates[1]}
                  anchor={p.status === 'geofenced' ? 'center' : 'bottom'}
                  onClick={() => onMarkerClick && onMarkerClick(p.id)}
                >
                  <img
                    src={generateMarkerIcon(p.status)}
                    alt="Marker"
                    className={`${p.id === selectedPointId || p.code === selectedPointId ? 'size-16' : 'size-10'}`}
                  />
                </Marker>
              ))}

            {mapLoaded && geofenceGeoJSON && (
              <Source
                id="geofence-source"
                type="geojson"
                data={geofenceGeoJSON as any}
              >
                {geofenceFillLayer && <Layer {...(geofenceFillLayer as any)} />}
                {geofenceStrokeLayer && (
                  <Layer {...(geofenceStrokeLayer as any)} />
                )}
              </Source>
            )}

            {mapLoaded &&
              imageOverlays?.map((overlay) => (
                <Source
                  key={overlay.id}
                  id={overlay.id}
                  type="image"
                  url={overlay.url}
                  coordinates={overlay.coordinates as any}
                >
                  <Layer
                    id={`${overlay.id}-layer`}
                    type="raster"
                    paint={{ 'raster-opacity': overlay.opacity ?? 0.85 }}
                  />
                </Source>
              ))}
            <AttributionControl position="bottom-right" compact />
          </Map>
        )}
      </div>
    );
  }
);

export default TrailMap;
